// Super-admin: PlayFirst Parent — Partner Discount Codes.
// /admin/partner-codes
//
// Lists every partner discount code with usage stats. Allows inline edit
// of the assigned collaborator + status, and create/delete of codes.
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowLeft, RefreshCw, Tag, Plus, Trash2, Loader2, Search, Copy, ExternalLink, Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

export default function AdminPartnerCodes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [paymentLink, setPaymentLink] = useState('');
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState({ code: '', assigned_collaborator: '', discount_percentage: 30 });
  const [copiedCode, setCopiedCode] = useState('');

  const auth = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/admin/partner-codes`, auth()),
        axios.get(`${API}/admin/partner-codes/stats`, auth()).catch(() => ({ data: null })),
      ]);
      setCodes(r.data.codes || []);
      setPaymentLink(r.data.payment_link || '');
      if (s?.data) setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Super-admin only');
        navigate('/');
      } else {
        toast.error("Couldn't load partner codes");
      }
    } finally {
      setLoading(false);
    }
  }, [auth, navigate]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return codes;
    return codes.filter((c) =>
      (c.code || '').toLowerCase().includes(q)
      || (c.assigned_collaborator || '').toLowerCase().includes(q),
    );
  }, [codes, search]);

  const updateField = async (id, patch) => {
    setSavingId(id);
    try {
      const r = await axios.put(`${API}/admin/partner-codes/${id}`, patch, auth());
      setCodes((arr) => arr.map((c) => (c.id === id ? { ...c, ...r.data } : c)));
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete code ${c.code}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/partner-codes/${c.id}`, auth());
      setCodes((arr) => arr.filter((x) => x.id !== c.id));
      toast.success(`Deleted ${c.code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    const code = (newCode.code || '').trim().toUpperCase();
    if (!code) { toast.error('Enter a code'); return; }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/admin/partner-codes`, {
        code,
        assigned_collaborator: newCode.assigned_collaborator.trim(),
        discount_percentage: Number(newCode.discount_percentage) || 30,
        status: 'active',
      }, auth());
      setCodes((arr) => [r.data, ...arr]);
      setNewCode({ code: '', assigned_collaborator: '', discount_percentage: 30 });
      toast.success(`Created ${r.data.code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = (code) => {
    try {
      navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 1200);
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-partner-codes-page">
      <header className="max-w-7xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-600 hover:text-stone-900"
          data-testid="partner-codes-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <Link to="/" className="text-sm text-stone-500 hover:underline">PlayFirst</Link>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 pb-12">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-[#7C6F9C]/10 text-[#7C6F9C] flex items-center justify-center">
              <Tag className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-serif text-stone-900">Partner Discount Codes</h1>
              <p className="text-sm text-stone-500">
                Manage reusable PlayFirst Parent discount codes and track which partner is referring whom.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            <Stat label="Total codes" value={stats.total_codes} testid="stat-total-codes" />
            <Stat label="Active" value={stats.active_codes} testid="stat-active-codes" />
            <Stat label="Code applies" value={stats.total_uses} testid="stat-total-uses" />
            <Stat label="Paid subs via codes" value={stats.active_subscriptions_via_partner_code} testid="stat-paid-via-codes" />
          </div>
        )}

        {/* Payment link callout */}
        {paymentLink && (
          <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid="partner-code-payment-link">
            <div>
              <p className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Discount payment link</p>
              <p className="text-sm text-stone-700 break-all mt-1">{paymentLink}</p>
            </div>
            <a
              href={paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-[#7C6F9C] hover:underline"
            >
              Open <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        )}

        {/* Create new */}
        <form onSubmit={handleCreate} className="mt-6 rounded-2xl border border-stone-200 bg-white p-4" data-testid="partner-code-create-form">
          <p className="text-sm font-medium text-stone-700 mb-3">Add a new code</p>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
            <Input
              className="sm:col-span-3 uppercase"
              placeholder="CODE30"
              value={newCode.code}
              onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
              data-testid="partner-code-new-code"
            />
            <Input
              className="sm:col-span-5"
              placeholder="Assigned collaborator (e.g. Bloom Therapy)"
              value={newCode.assigned_collaborator}
              onChange={(e) => setNewCode({ ...newCode, assigned_collaborator: e.target.value })}
              data-testid="partner-code-new-collaborator"
            />
            <Input
              className="sm:col-span-2"
              type="number"
              min={1}
              max={100}
              value={newCode.discount_percentage}
              onChange={(e) => setNewCode({ ...newCode, discount_percentage: e.target.value })}
              data-testid="partner-code-new-discount"
            />
            <Button
              type="submit"
              disabled={creating}
              className="sm:col-span-2 bg-[#7C6F9C] hover:bg-[#6a5d8a]"
              data-testid="partner-code-create-submit"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {creating ? 'Adding…' : 'Add code'}
            </Button>
          </div>
        </form>

        {/* Toolbar */}
        <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              className="pl-9"
              placeholder="Search by code or collaborator"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="partner-code-search"
            />
          </div>
          <Button
            variant="outline"
            onClick={fetchCodes}
            disabled={loading}
            data-testid="partner-code-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Table */}
        <div className="mt-4 rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="partner-codes-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr className="text-left text-xs uppercase tracking-wider text-stone-500">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Discount</th>
                  <th className="px-4 py-3">Assigned to</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Date assigned</th>
                  <th className="px-4 py-3 text-right">Uses</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-right">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-stone-500">
                    <Loader2 className="w-5 h-5 inline animate-spin" /> Loading…
                  </td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-stone-500">
                    No codes match.
                  </td></tr>
                )}
                {!loading && filtered.map((c) => (
                  <tr key={c.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/50" data-testid={`partner-code-row-${c.code}`}>
                    <td className="px-4 py-3 font-mono text-sm font-semibold text-stone-900">
                      <div className="flex items-center gap-2">
                        {c.code}
                        <button
                          type="button"
                          onClick={() => handleCopy(c.code)}
                          className="text-stone-400 hover:text-stone-700"
                          aria-label="Copy code"
                          data-testid={`partner-code-copy-${c.code}`}
                        >
                          {copiedCode === c.code ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-stone-700">{c.discount_percentage}%</td>
                    <td className="px-4 py-3 min-w-[180px]">
                      <InlineEdit
                        value={c.assigned_collaborator || ''}
                        placeholder="—"
                        disabled={savingId === c.id}
                        onSave={(v) => updateField(c.id, { assigned_collaborator: v })}
                        testid={`partner-code-collab-${c.code}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => updateField(c.id, { status: c.status === 'active' ? 'inactive' : 'active' })}
                        disabled={savingId === c.id}
                        data-testid={`partner-code-status-${c.code}`}
                      >
                        <Badge className={c.status === 'active'
                          ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                          : 'bg-stone-200 text-stone-700 hover:bg-stone-300'}>
                          {c.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-stone-600 text-xs">{fmt(c.date_assigned)}</td>
                    <td className="px-4 py-3 text-right text-stone-700">{c.number_of_uses || 0}</td>
                    <td className="px-4 py-3 text-stone-500 text-xs">{fmt(c.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleDelete(c)}
                        className="text-rose-600 hover:text-rose-700"
                        aria-label={`Delete ${c.code}`}
                        data-testid={`partner-code-delete-${c.code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-stone-400 mt-3">
          Showing {filtered.length} of {codes.length} codes. Codes are case-insensitive at validation.
        </p>
      </main>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{label}</p>
      <p className="text-2xl font-serif text-stone-900 mt-1">{value ?? 0}</p>
    </div>
  );
}

function InlineEdit({ value, placeholder, onSave, disabled, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  useEffect(() => { setDraft(value || ''); }, [value]);

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-left text-sm text-stone-700 hover:text-stone-900 hover:underline"
        data-testid={testid}
      >
        {value || <span className="text-stone-400">{placeholder}</span>}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Input
        className="h-8"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') { onSave(draft.trim()); setEditing(false); }
          if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
        }}
      />
      <Button
        size="sm"
        variant="outline"
        onClick={() => { onSave(draft.trim()); setEditing(false); }}
        disabled={disabled}
      >
        Save
      </Button>
    </div>
  );
}
